/**
 * Frontend helpers for resolving and preserving Execution Sub Groups.
 *
 * DQ-8684 resolves the display name client-side from the
 * `/execution-sub-group` list because execution-result rows only carry the id
 * and denormalised code.
 *
 * `run_name` on the same rows is the Execution Group (ex-Run Instance) name —
 * a separate value that must never be used as the sub group name.
 *
 * DQ-8820 preserves the same sub group across validation re-runs, including
 * executions whose denormalised columns are missing from the row but present in
 * the nested request body.
 */

export interface IExecutionSubGroupOption {
  id: number | string;
  name?: string | null;
  code?: string | null;
  execution_sub_group_code?: string | null;
}

export interface IExecutionSubGroupRow {
  execution_sub_group_id?: number | string | null;
  execution_sub_group_code?: string | null;
}

export interface IExecutionSubGroupEntry {
  name: string;
  code: string;
}

export interface IExecutionSubGroupRef {
  execution_sub_group_id: number | string | null;
  execution_sub_group_code: string | null;
}

export type ExecutionSubGroupMap = Map<string, IExecutionSubGroupEntry>;

const normalizeExecutionSubGroupId = (
  value: number | string | null | undefined,
): number | string | null => {
  if (value == null || value === "" || value === 0 || value === "0") {
    return null;
  }
  return value;
};

/**
 * Builds an `id -> { name, code }` lookup from the `/execution-sub-group` list.
 * Ids are keyed as strings so numeric and string ids resolve identically.
 */
export const buildExecutionSubGroupMap = (
  options: IExecutionSubGroupOption[] | null | undefined,
): ExecutionSubGroupMap => {
  const map: ExecutionSubGroupMap = new Map();
  (options || []).forEach((option) => {
    if (option?.id == null) return;
    map.set(String(option.id), {
      name: option.name || "",
      code: option.execution_sub_group_code || option.code || "",
    });
  });
  return map;
};

/**
 * DQ-8820: executions started as part of another execution do not always carry
 * the denormalised `execution_sub_group_*` columns on the row, even though the
 * request body still contains them. Check both locations for re-run support.
 */
export const getExecutionSubGroupRef = (
  execution: any,
): IExecutionSubGroupRef => {
  const requestBodyGroup =
    execution?.complete_validation_params?.validation_request_body
      ?.execution_sub_group;
  const id = normalizeExecutionSubGroupId(
    execution?.execution_sub_group_id ??
      requestBodyGroup?.execution_sub_group_id ??
      null,
  );
  const code =
    execution?.execution_sub_group_code ||
    requestBodyGroup?.execution_sub_group_code ||
    null;

  return { execution_sub_group_id: id, execution_sub_group_code: code };
};

/**
 * Resolves an Execution Sub Group display name for a result row.
 *
 * Fallback chain: looked-up name → row's own code → looked-up code → raw id → "".
 */
export const getExecutionSubGroupName = (
  row: IExecutionSubGroupRow | null | undefined,
  map?: ExecutionSubGroupMap | null,
): string => {
  const id = normalizeExecutionSubGroupId(row?.execution_sub_group_id);
  const resolved = id == null ? undefined : map?.get(String(id));
  if (resolved?.name) return resolved.name;
  if (row?.execution_sub_group_code) return row.execution_sub_group_code;
  if (resolved?.code) return resolved.code;
  return id == null ? "" : String(id);
};

/**
 * Resolves the Autocomplete option for the currently selected sub group.
 *
 * DQ-8820: the options list only holds active sub groups, so a re-run whose
 * sub group is absent from that list still needs a synthetic visible option.
 */
export const resolveExecutionSubGroupOption = (
  options: IExecutionSubGroupOption[] | null | undefined,
  selected: IExecutionSubGroupRef | IExecutionSubGroupRow | null | undefined,
): IExecutionSubGroupOption | null => {
  const id = normalizeExecutionSubGroupId(selected?.execution_sub_group_id);
  if (id == null) return null;

  const match = (options || []).find(
    (option) => option?.id != null && String(option.id) === String(id),
  );
  if (match) return match;

  const code = selected?.execution_sub_group_code || "";
  return {
    id,
    name: code || String(id),
    code,
    execution_sub_group_code: code,
  };
};
